import {
  App,
  Button,
  Col,
  Collapse,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
  UploadFile
} from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { LANGUAGES } from 'constants/languages';
import {
  SERVICE_STATUSES,
  SERVICE_TYPES,
  SERVICE_TYPES_VALUES
} from 'constants/service';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CloudUploadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  attachServiceToDoctors,
  createService,
  fetchServiceById,
  updateService
} from 'services/api/services';
import { ServiceDetail, ServiceInput, ServiceType } from 'types/api/services';

import { CKEditor } from 'components/shared';
import { Loading, PageWrapper } from 'components/ui';
import Dragger from 'components/shared/Dragger';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';
import DoctorsSelect from './components/DoctorsSelect';
import { getInputDir } from 'utils/helpers';

const { Title, Paragraph, Text } = Typography;

const itemValues = {
  title: '',
  description: '',
  short_desc: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  img: '',
  type: '',
  status: '',
  link: '',
  text: ''
};

type ValuesType = typeof itemValues;
type BodyType = Record<string, ValuesType>;

const ServicesDetails: FC = () => {
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  const [file, setFile] =
    useState<UploadChangeParam<UploadFile<{ data: string }>>>();

  const [secondaryImg, setSecondaryImg] =
    useState<UploadChangeParam<UploadFile<{ data: string }>>>();

  const [icon, setIcon] =
    useState<UploadChangeParam<UploadFile<{ data: string }>>>();

  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const params = useParams();
  const navigate = useNavigate();
  const [serviceType, setServiceType] = useState<ServiceType | undefined>();

  const serviceId = params?.id;

  const [result, setResult] = useState<ServiceDetail | null>(null);
  const [isFetching, setIsFetching] = useState(!!serviceId);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!serviceId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsFetching(true);
        const detail = await fetchServiceById(serviceId);
        if (cancelled) return;
        setResult(detail);
        if (detail.type != null) setServiceType(detail.type);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const isImageRequired =
    serviceType === SERVICE_TYPES_VALUES['radiology'] ||
    serviceType === SERVICE_TYPES_VALUES['video_call'];

  const onFinish = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: Record<string, any>) => {
      const doctors = values.doctors;
      delete values.doctors;

      setErrorKeys([]);
      const input: ServiceInput = {
        ...(values as ServiceInput),
        main_image:
          file?.file?.response?.data || result?.main_image || undefined,
        img: secondaryImg?.file?.response?.data || result?.img || undefined,
        icon: icon?.file?.response?.data || result?.icon || undefined,
        type: values.type,
        status: values.status
      };

      try {
        setIsSaving(true);
        let nextId: number | undefined;

        if (serviceId) {
          await updateService(serviceId, input);
          nextId = Number(serviceId);
        } else {
          const res = await createService(input);
          nextId = res.data?.id;
        }

        if (nextId != null) {
          await attachServiceToDoctors({
            service_id: nextId,
            doctor_ids: (doctors || []).map(Number)
          });
        }

        message.success('Successfully saved!');
        navigate('/services');
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    [
      file?.file?.response?.data,
      result?.main_image,
      result?.img,
      result?.icon,
      secondaryImg?.file?.response?.data,
      icon?.file?.response?.data,
      serviceId,
      message,
      navigate
    ]
  );

  const onFinishError = (errorInfo: ValidateErrorEntity<BodyType>) => {
    setErrorKeys([]);
    const errors = new Set(
      errorInfo.errorFields.map(item => `${item.name[0]}`)
    );

    setErrorKeys(Array.from(errors));
  };

  return (
    <PageWrapper
      title={serviceId ? t('permissions.UPDATE') : t('permissions.CREATE')}
      extra={[
        <Button
          type="primary"
          key="save-service"
          loading={isSaving}
          onClick={form.submit}
          size="large"
        >
          {t('common.save')}
        </Button>
      ]}
    >
      {isFetching ? (
        <Loading />
      ) : (
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishError}
          layout="vertical"
          size="large"
          initialValues={{
            ...result,
            doctors: result?.doctor_ids ?? []
          }}
          scrollToFirstError
        >
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label={t('service.main_image')}
                name="main_image"
                valuePropName="main_image"
                rules={[{ required: true }]}
              >
                <Dragger
                  listType="picture"
                  maxCount={1}
                  multiple={false}
                  accept={'image/jpeg, image/png'}
                  onChange={info => {
                    setFile(info);
                  }}
                  defaultFileList={
                    result?.main_image
                      ? [
                          {
                            name: result.main_image || '',
                            uid: result.main_image || '',
                            thumbUrl: getFileUrl(result?.main_image)
                          }
                        ]
                      : []
                  }
                >
                  <Flex vertical align="center">
                    <CloudUploadOutlined
                      style={{
                        color: 'var(--primary-color-100)',
                        fontSize: 45
                      }}
                    />
                    <Title level={4}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('service.drag_drop_files_or_browse')
                        }}
                      />
                    </Title>
                    <Paragraph style={{ color: 'var(--text-color-80)' }}>
                      {t('service.supported_formats')}
                    </Paragraph>
                  </Flex>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('service.secondary_image')}
                name="img"
                valuePropName="img"
                rules={[
                  {
                    required: isImageRequired
                  }
                ]}
              >
                <Dragger
                  listType="picture"
                  maxCount={1}
                  multiple={false}
                  accept={'image/jpeg, image/png'}
                  onChange={info => {
                    setSecondaryImg(info);
                  }}
                  defaultFileList={
                    result?.img
                      ? [
                          {
                            name: result.img || '',
                            uid: result?.img || '',
                            thumbUrl: getFileUrl(result?.img)
                          }
                        ]
                      : []
                  }
                >
                  <Flex vertical align="center">
                    <CloudUploadOutlined
                      style={{
                        color: 'var(--primary-color-100)',
                        fontSize: 45
                      }}
                    />
                    <Title level={4}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('service.drag_drop_files_or_browse')
                        }}
                      />
                    </Title>
                    <Paragraph style={{ color: 'var(--text-color-80)' }}>
                      {t('service.supported_formats')}
                    </Paragraph>
                  </Flex>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }} gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name="link"
                label={t('service.link')}
                rules={[{ required: true }]}
              >
                <Input addonBefore="https://" type="link" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label={t('service.type')}
                rules={[{ required: true }]}
              >
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={SERVICE_TYPES}
                  onChange={e => setServiceType(e)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('service.status')}
                rules={[{ required: true }]}
              >
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={SERVICE_STATUSES}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="icon"
                label={t('service.upload_icon')}
                rules={[{ required: true }]}
                valuePropName="icon"
              >
                <Dragger
                  style={{ padding: 0, border: 'none' }}
                  listType="picture"
                  maxCount={1}
                  multiple={false}
                  accept={'image/jpeg, image/png'}
                  onChange={info => {
                    setIcon(info);
                  }}
                  defaultFileList={
                    result?.icon
                      ? [
                          {
                            name: result.icon || '',
                            uid: result?.icon || '',
                            thumbUrl: getFileUrl(result?.icon)
                          }
                        ]
                      : []
                  }
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: '100%', overflow: 'hidden' }}
                  >
                    <Text ellipsis>{t('service.upload_icon')}</Text>
                  </Button>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <DoctorsSelect />
            </Col>
          </Row>
          <Collapse
            accordion
            destroyInactivePanel={false}
            defaultActiveKey={LANGUAGES[0].locale}
            items={LANGUAGES.map(item => {
              const hasError = errorKeys.includes(item.locale);

              return {
                key: item.locale,
                forceRender: true,
                label: (
                  <Text
                    style={{
                      color: `var(--${hasError ? 'error-color' : 'text-color'})`
                    }}
                  >
                    {item.label}
                  </Text>
                ),
                children: (
                  <>
                    <Form.Item
                      name={['keywords', item.locale, 'meta_title']}
                      label={t('service.meta_title')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['keywords', item.locale, 'meta_description']}
                      label={t('service.meta_description')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['keywords', item.locale, 'meta_keywords']}
                      label={t('service.meta_keywords')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['title', item.locale]}
                      label={t('service.title')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['subtitle', item.locale]}
                      label={t('service.subtitle')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['short_desc', item.locale]}
                      label={t('service.short_desc')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={['description', item.locale]}
                      label={t('service.description')}
                      rules={[{ required: true }]}
                    >
                      <CKEditor language={item.locale} />
                    </Form.Item>
                    <Form.Item
                      name={['text', item.locale]}
                      label={t('service.benefit')}
                      rules={[{ required: true }]}
                    >
                      <CKEditor language={item.locale} />
                    </Form.Item>
                  </>
                )
              };
            })}
          />
        </Form>
      )}
    </PageWrapper>
  );
};

export default ServicesDetails;
